import type { PageModule } from "./page-module"

/**
 * 各セクションをスクロール進入の進行度で映画的に「登場」させる。
 * scroll-peel-sections（addict_intraction）の入場エフェクトを移植したもので、
 * scale / blur / brightness / opacity / translateY を power3.out で補間する。
 * ※sticky積み重ねによる「剥がれ」はやらない（可変高さ・既存インタラクションと共存するため）。
 * 対象: [data-peel]
 */

/** rect.top === innerHeight * START_RATIO で進行度0（画面下端に上端がある） */
const START_RATIO = 1.0
/** rect.top === innerHeight * END_RATIO で進行度1（ここまで上がると演出完了） */
const END_RATIO = 0.6

/** 登場エフェクトの始点→終点（scaleは transform-origin: center top で上端固定＝上揃え） */
const ENTER = {
  scale: { from: 0.94, to: 1 },
  blur: { from: 8, to: 0 }, // px
  brightness: { from: 0.75, to: 1 },
  opacity: { from: 0.35, to: 1 },
}

/** min-max にクランプ */
const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v))

/** from→to を t(0-1) で線形補間 */
const lerp = (from: number, to: number, t: number): number => from + (to - from) * t

/** power3.out: 1-(1-t)^3 */
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

/**
 * 1セクションに進行度を計算して inline style を適用（定常は解除）
 * @param {HTMLElement} section - 対象セクション
 */
const applyPeel = (section: HTMLElement): void => {
  const vh = window.innerHeight
  const top = section.getBoundingClientRect().top
  const start = vh * START_RATIO
  const end = vh * END_RATIO
  const p = clamp((start - top) / (start - end), 0, 1)
  const e = easeOut(p)

  // 定常：インラインを消して素の状態へ（filter/transformレイヤーを残さない）
  if (e >= 0.999) {
    section.style.transform = ""
    section.style.filter = ""
    section.style.opacity = ""
    section.style.willChange = ""
    return
  }

  const scale = lerp(ENTER.scale.from, ENTER.scale.to, e)
  const blur = lerp(ENTER.blur.from, ENTER.blur.to, e)
  const brightness = lerp(ENTER.brightness.from, ENTER.brightness.to, e)
  const opacity = lerp(ENTER.opacity.from, ENTER.opacity.to, e)

  section.style.willChange = "transform, filter, opacity"
  section.style.transform = `translate3d(0, 0, 0) scale(${scale})`
  section.style.filter = `blur(${blur}px) brightness(${brightness})`
  section.style.opacity = String(opacity)
}

/**
 * 初期化：スクロール/リサイズを rAF スロットルで更新。reduced-motion はスキップ。
 * scope（現在ページの <main>）配下の [data-peel] を対象に mount し、後始末関数を返す。
 * @param {HTMLElement} scope - 現在ページの <main>
 * @returns {(() => void) | void} リスナー/rAF を解除する destroy 関数（対象が無ければ void）
 */
export const mountSectionPeel: PageModule = (scope) => {
  const sections = Array.from(scope.querySelectorAll<HTMLElement>("[data-peel]"))
  if (!sections.length) return
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

  // 上端を固定して縮小＝上揃え（上に隙間が出ない）
  sections.forEach((s) => {
    s.style.transformOrigin = "center top"
  })

  let rafId: number | null = null

  const update = (): void => {
    sections.forEach(applyPeel)
  }

  const onScroll = (): void => {
    if (rafId !== null) return
    rafId = requestAnimationFrame(() => {
      rafId = null
      update()
    })
  }

  const ac = new AbortController()
  window.addEventListener("scroll", onScroll, { passive: true, signal: ac.signal })
  window.addEventListener("resize", onScroll, { passive: true, signal: ac.signal })
  update() // 初回

  return () => {
    ac.abort()
    if (rafId !== null) cancelAnimationFrame(rafId)
  }
}
